import React, { useState, useEffect } from 'react';
import PageContainer from '../components/containers/PageContainer';
import {
    Card,
    CardHeader,
    CardContent,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    Select,
    MenuItem,
    Box,
    Button,
    Grid,
    Typography,
    Paper,
    Chip,
} from '@mui/material';
import api from '../config/axios';
import { toast } from 'react-toastify';
import Field from '../components/ui/Field';
import CustomTable from '../components/ui/CustomTable';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ModalContainer from '../components/containers/ModalContainer';
import StatusComp from '../components/common/StatusComp';
import dayjs from 'dayjs';
import DateFilter from '../components/common/DateFilter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TrackingStatusChip from '../components/ui/TrackingStatusChip';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const TRACKING_STATUS_CATEGORIES = {
    OUT_FOR_DELIVERY: ['out for delivery', 'out-for-delivery'],
    DELIVERED: ['delivered', 'complete'],
    PRE_SHIPMENT: ['pre-shipment', 'acceptance', 'pre shipment', 'label created', 'shipment information received'],
    IN_TRANSIT: [
        'in transit',
        'in-transit',
        'intransit',
        'transit',
        'moving',
        'departed',
        'arrived at hub',
        'arrived',
        'processed',
        'sorting',
    ],
    EXCEPTION: [
        'exception',
        'error',
        'failure',
        'return',
        'failed',
        'returned',
        'delay',
        'held',
        'alert',
        'undeliverable as addressed',
        'undeliverable',
        'returned to sender',
        'addressee unknown',
        'available for pickup',
        'forwarded',
        'held at post office per customer request',
        'held at post office',
        'package intercepted',
        'intercepted',
        'damaged',
        'damaged – arrived at usps facility',
        'seized by law enforcement',
        'seized',
        'notice left',
        'delivery attempted',
        'refused',
        'insufficient address',
        'no such number',
        'no access',
    ],
    UNKNOWN: ['unknown', 'no information', 'not found', 'no tracking information', ''],
};

const Orders = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // States for orders data
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loader, setLoader] = useState(false);
    const [message, setMessage] = useState('');
    const [order, setOrder] = useState({});
    const [orderContent, setOrderContent] = useState({});
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [pricings, setPricings] = useState([]);
    const [stats, setStats] = useState({
        noTrackingInfo: 0,
        inTransit: 0,
        delivered: 0,
        exception: 0,
    });
    // States for filters
    const [filter, setFilter] = useState(searchParams.get('status') || 'All');
    const [sort, setSort] = useState(searchParams.get('sort') || 'asc');
    const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
    const [limit, setLimit] = useState(parseInt(searchParams.get('limit') || '10', 10));
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
    const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'All');
    const [selectedTrackingStatus, setSelectedTrackingStatus] = useState(searchParams.get('trackingstatus') || 'All');
    const [dateRange, setDateRange] = useState({
        startDate: searchParams.get('from') ? dayjs(searchParams.get('from')).toDate() : null,
        endDate: searchParams.get('to') ? dayjs(searchParams.get('to')).toDate() : null,
        key: 'selection',
    });

    const isExceptionStatus = (trackingEvents) => {
        if (!trackingEvents || trackingEvents.length === 0) return false;
        const latestEvent = trackingEvents[0];
        if (!latestEvent || !latestEvent.Event) return false;
        const eventText = latestEvent.Event.toLowerCase();
        return TRACKING_STATUS_CATEGORIES.EXCEPTION.some((exceptionStatus) =>
            eventText.includes(exceptionStatus.toLowerCase())
        );
    };
    const getPricings = async () => {
        await api
            .get('/labeltype/read')
            .then((res) => {
                setPricings(res.data.labelTypes);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        getPricings();
    }, []);
    // Modal functions
    const onCloseOrderDetailsModal = () => setShowOrderDetails(false);
    const onOpenOrderDetailsModal = () => setShowOrderDetails(true);

    // Update URL with current filter state
    const updateUrl = () => {
        const params = new URLSearchParams();

        if (search) params.set('search', search);
        if (filter !== 'All') params.set('status', filter);
        if (selectedType !== 'All') params.set('type', selectedType);
        if (selectedTrackingStatus !== 'All') params.set('trackingstatus', selectedTrackingStatus);
        if (sort) params.set('sort', sort);
        if (page > 1) params.set('page', page.toString());
        if (limit !== 10) params.set('limit', limit.toString());
        if (dateRange.startDate) params.set('from', dayjs(dateRange.startDate).format('YYYY-MM-DD'));
        if (dateRange.endDate) params.set('to', dayjs(dateRange.endDate).format('YYYY-MM-DD'));

        setSearchParams(params);
    };

    // Fetch orders from API
    const getOrders = async () => {
        setLoader(true);

        try {
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('limit', limit.toString());
            params.append('sort', sort);
            params.append('status', filter);

            if (search) params.append('search', search);
            if (selectedType !== 'All') params.append('type', selectedType);
            if (selectedTrackingStatus !== 'All') params.append('trackingstatus', selectedTrackingStatus);
            if (dateRange.startDate) params.append('from', dayjs(dateRange.startDate).format('YYYY-MM-DD'));
            if (dateRange.endDate) params.append('to', dayjs(dateRange.endDate).format('YYYY-MM-DD'));

            const link = `/admin/orders/read?${params.toString()}`;
            const res = await api.get(link);

            setOrders(res.data.orders);
            setTotalPages(res.data.totalPages);
            setTotal(res.data.total);
            if (res.data.stats) {
                setStats(res.data.stats);
            }
        } catch (err) {
            console.error(err);
            setMessage('Failed to fetch orders');
        } finally {
            setLoader(false);
        }
    };

    // Handle search button click
    const handleSearch = () => {
        setSearch(searchInput);
        setPage(1); // Reset to first page on new search
    };

    // Check if any filter is active
    const hasActiveFilters = () => {
        return (
            search !== '' ||
            filter !== 'All' ||
            selectedType !== 'All' ||
            selectedTrackingStatus !== 'All' ||
            sort !== 'asc' ||
            limit !== 10 ||
            dateRange.startDate !== null ||
            dateRange.endDate !== null ||
            page > 1
        );
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setSearchInput('');
        setFilter('All');
        setSelectedType('All');
        setSelectedTrackingStatus('All');
        setSort('asc');
        setLimit(10);
        setPage(1);
        setDateRange({
            startDate: null,
            endDate: null,
            key: 'selection',
        });
        setSearchParams(new URLSearchParams());
    };

    // Sync URL params with state
    useEffect(() => {
        updateUrl();
    }, [filter, sort, page, limit, search, selectedType, selectedTrackingStatus, dateRange]);

    // Fetch orders when URL params change
    useEffect(() => {
        getOrders();
    }, [searchParams]);

    const updateOrder = async (e) => {
        e.preventDefault();

        const params = new FormData();
        params.append('pdf', e.target.pdf.files[0]);

        await api
            .post('/admin/updateOrder/' + order._id, params)
            .then((res) => {
                toast.success(res.data.message);
                setLoader('');
                getOrders();
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            });

        setTimeout(() => {
            setMessage('');
            e.target.reset();
        }, 3000);
    };

    const API_UIDs = ['api_ground_oz_v2', 'api_ground_v2', 'api_priority_v2'];

    const StatCard = ({ icon: Icon, value, label, bgColor }) => (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
            }}>
            <Box
                sx={{
                    backgroundColor: bgColor,
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <Icon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
                <Typography variant="h5" fontWeight="600" color="text.primary">
                    {value?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            </Box>
        </Paper>
    );

    return (
        <PageContainer title="View Orders" desc="All your active orders at one place">
            {/* Stats Cards Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={6} lg={2.4}>
                    <StatCard icon={ShoppingCartIcon} value={total} label="Total Orders" bgColor="primary.main" />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2.4}>
                    <StatCard
                        icon={InfoOutlinedIcon}
                        value={stats.noTrackingInfo}
                        label="No Tracking Info"
                        bgColor="warning.main"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2.4}>
                    <StatCard icon={LocalShippingIcon} value={stats.inTransit} label="In Transit" bgColor="info.main" />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2.4}>
                    <StatCard
                        icon={CheckCircleOutlineIcon}
                        value={stats.delivered}
                        label="Delivered"
                        bgColor="success.main"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2.4}>
                    <StatCard icon={ErrorOutlineIcon} value={stats.exception} label="Exception" bgColor="error.main" />
                </Grid>
            </Grid>

            {/* Filters Section */}
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Search Bar */}
                <Box sx={{ display: 'flex', direction: 'row', gap: 1, alignItems: 'center' }}>
                    <Field
                        placeholder="Search orders using order id, name, email address"
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && searchInput.length >= 4) {
                                handleSearch();
                            }
                        }}
                        sx={{ flex: 1, minWidth: '250px' }}
                    />
                    <Button
                        variant="contained"
                        size="large"
                        disabled={searchInput.length < 4}
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{ minWidth: '100px' }}>
                        Search
                    </Button>
                </Box>

                {/* Filter Controls */}
                <Box sx={{ display: 'flex', direction: 'row', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Type:</span>
                        <Select
                            size="small"
                            value={selectedType}
                            onChange={(e) => {
                                setSelectedType(e.target.value);
                                setPage(1);
                            }}
                            sx={{ minWidth: '150px' }}>
                            <MenuItem value="All">All Types</MenuItem>
                            {pricings.map((pricing) => (
                                <MenuItem key={pricing._id} value={pricing.name}>
                                    {pricing.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Tracking Status:</span>
                        <Select
                            size="small"
                            value={selectedTrackingStatus}
                            onChange={(e) => {
                                setSelectedTrackingStatus(e.target.value);
                                setPage(1);
                            }}
                            sx={{ minWidth: '180px' }}>
                            <MenuItem value="All">All Statuses</MenuItem>
                            <MenuItem value="EXCEPTION">Exception</MenuItem>
                            <MenuItem value="DELIVERED">Delivered</MenuItem>
                            <MenuItem value="IN_TRANSIT">In Transit</MenuItem>
                            <MenuItem value="OUT_FOR_DELIVERY">Out for Delivery</MenuItem>
                            <MenuItem value="PRE_SHIPMENT">Pre Shipment</MenuItem>
                            <MenuItem value="UNKNOWN">Unknown</MenuItem>
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Sort:</span>
                        <Select
                            size="small"
                            value={sort}
                            onChange={(e) => {
                                setSort(e.target.value);
                            }}
                            sx={{ minWidth: '120px' }}>
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Limit:</span>
                        <Select
                            size="small"
                            value={limit}
                            onChange={(e) => {
                                setLimit(e.target.value);
                                setPage(1);
                            }}
                            sx={{ minWidth: '80px' }}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DateFilter dateRange={dateRange} setDateRange={setDateRange} apply={getOrders} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {hasActiveFilters() && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={handleClearFilters}
                                startIcon={<ClearIcon />}
                                sx={{ minWidth: '120px' }}>
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            <CustomTable
                sx={{ mt: 2 }}
                fields={[
                    'ORDER ID',
                    'USER',
                    'TOTAL',
                    'LABELTYPE',
                    'TRACKING',
                    'MASK TRACKING',
                    'STATUS',
                    'TRACKING STATUS',
                    'CREATED AT',
                    'ACTION',
                ]}
                dense={false}
                loading={loader}
                pagination
                count={totalPages}
                page={page}
                setPage={setPage}>
                {orders.map((order, index) => (
                    <TableRow key={order._id}>
                        <TableCell>{order._id}</TableCell>
                        <TableCell>{order.user?.username}</TableCell>
                        <TableCell
                            sx={{
                                color: 'success.main',
                            }}>
                            ${order.price ? order.price.toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell>{order.labelType?.name}</TableCell>
                        <TableCell
                            sx={{
                                color: 'primary.main',
                            }}>
                            {order.tracking}
                        </TableCell>
                        <TableCell
                            sx={{
                                color: 'primary.main',
                            }}>
                            {API_UIDs.includes(order.labelType?.uid) ? order.maskedTracking : 'N/A'}
                        </TableCell>
                        <TableCell>
                            <StatusComp status={order.status} />
                        </TableCell>
                        <TableCell>
                            <TrackingStatusChip
                                trackingData={{
                                    events: order?.trackingDetails || [],
                                }}
                            />
                        </TableCell>
                        <TableCell>{new Date(order.createdAt).toLocaleString()}</TableCell>
                        <TableCell>
                            <Tooltip title="View Order Details">
                                <IconButton
                                    onClick={() => {
                                        setOrder(order);
                                        onOpenOrderDetailsModal();
                                    }}>
                                    <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                ))}
            </CustomTable>
            <ModalContainer open={showOrderDetails} onClose={onCloseOrderDetailsModal} hideActions title="View Order">
                <div className="row">
                    <div className="col-lg-6">
                        <Card>
                            <CardHeader title="From" />

                            <CardContent>
                                <div className="card-text">
                                    <span className="fw-bold">Name:</span> {order.FromName} <br />
                                    <span className="fw-bold">Company:</span> {order.FromCompany} <br />
                                    <span className="fw-bold">Street:</span> {order.FromStreet} <br />
                                    <span className="fw-bold">Street2:</span> {order.FromStreet2} <br />
                                    <span className="fw-bold">City:</span> {order.FromCity} <br />
                                    <span className="fw-bold">State:</span> {order.FromState} <br />
                                    <span className="fw-bold">Zip:</span> {order.FromZip} <br />
                                    <span className="fw-bold">Country:</span> {order.FromCountry} <br />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="col-lg-6">
                        <Card>
                            <CardHeader title="To" />
                            <CardContent>
                                <div className="card-text">
                                    <span className="fw-bold">Name:</span> {order.ToName} <br />
                                    <span className="fw-bold">Company:</span> {order.ToCompany} <br />
                                    <span className="fw-bold">Street:</span> {order.ToStreet} <br />
                                    <span className="fw-bold">Street2:</span> {order.ToStreet2} <br />
                                    <span className="fw-bold">City:</span> {order.ToCity} <br />
                                    <span className="fw-bold">State:</span> {order.ToState} <br />
                                    <span className="fw-bold">Zip:</span> {order.ToZip} <br />
                                    <span className="fw-bold">Country:</span> {order.ToCountry} <br />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <div className="row mt-3 mb-3">
                    <div className="col-lg-12">
                        <Card>
                            <CardHeader title="Additional details" />

                            <CardContent>
                                <div className="card-text">
                                    <span className="fw-bold">Label Type:</span> {order.labelType?.name} <br />
                                    <span className="fw-bold">Mask Tracking:</span>{' '}
                                    {API_UIDs.includes(order.labelType?.uid) ? order.maskedTracking : 'N/A'} <br />
                                    <span className="fw-bold">Tracking:</span> {order.tracking || 'N/A'} <br />
                                    <span className="fw-bold">Label Type:</span> {order.labelType?.name} <br />
                                    <span className="fw-bold">Weight:</span> {order.Weight}
                                    {order.labelType?.name.includes('First Class') ? (
                                        <span className="fw-bold">oz</span>
                                    ) : (
                                        <span className="fw-bold">lb</span>
                                    )}{' '}
                                    <br />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </ModalContainer>
        </PageContainer>
    );
};

export default Orders;
